/**
 * The one place that talks to the network. Components never call `fetch`: they
 * call a hook from `./queries`, which calls an endpoint from `./endpoints`,
 * which goes through here. Every response is parsed with a zod schema from
 * @mamimap/shared, so a contract change surfaces at the door instead of as an
 * undefined deep inside a table cell.
 */
import { apiErrorSchema } from '@mamimap/shared';
import type { z } from 'zod';

/** Empty means: use the Vite proxy, which forwards /api to 127.0.0.1:8020. */
const BASIS_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** Alles loopt over de versie-prefix, inloggen inbegrepen. */
export const API_URL = `${BASIS_URL}/api/v1`;

/** Thrown for every non-2xx response and for an answer that fails validation. */
export class ApiFout extends Error {
  readonly statusCode: number;
  readonly foutcode: string;

  constructor(statusCode: number, foutcode: string, bericht: string) {
    super(bericht);
    this.name = 'ApiFout';
    this.statusCode = statusCode;
    this.foutcode = foutcode;
  }
}

export type QueryWaarde = string | number | boolean | readonly string[] | null | undefined;

/** Arrays become `a,b,c`: the filter schema in @mamimap/shared reads both forms. */
export function bouwQuery(params: Record<string, QueryWaarde> | undefined): string {
  if (!params) return '';
  const zoek = new URLSearchParams();
  for (const [sleutel, waarde] of Object.entries(params)) {
    if (waarde === undefined || waarde === null || waarde === '') continue;
    if (Array.isArray(waarde)) {
      if (waarde.length > 0) zoek.set(sleutel, waarde.join(','));
    } else {
      zoek.set(sleutel, String(waarde));
    }
  }
  const query = zoek.toString();
  return query ? `?${query}` : '';
}

type VerzoekOpties = {
  params?: Record<string, QueryWaarde>;
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: BodyInit;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  basis?: string;
};

export async function verzoek<T extends z.ZodType>(
  pad: string,
  schema: T,
  opties: VerzoekOpties = {},
): Promise<z.infer<T>> {
  const basis = opties.basis ?? API_URL;
  let response: Response;
  try {
    response = await fetch(`${basis}${pad}${bouwQuery(opties.params)}`, {
      method: opties.methode ?? 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', ...opties.headers },
      body: opties.body,
      signal: opties.signal,
    });
  } catch {
    throw new ApiFout(0, 'geen_verbinding', 'Geen verbinding met de server.');
  }

  const tekst = await response.text();
  const body: unknown = tekst.length > 0 ? veiligJson(tekst) : null;

  if (!response.ok) throw naarApiFout(response.status, body);

  const resultaat = schema.safeParse(body);
  if (!resultaat.success) {
    throw new ApiFout(500, 'ongeldig_antwoord', 'De server gaf een onverwacht antwoord.');
  }
  return resultaat.data;
}

function veiligJson(tekst: string): unknown {
  try {
    return JSON.parse(tekst);
  } catch {
    return null;
  }
}

/** Leest de foutvorm van de API, met een terugval voor een antwoord dat er buiten valt. */
function naarApiFout(status: number, body: unknown): ApiFout {
  const api = apiErrorSchema.safeParse(body);
  if (api.success) return new ApiFout(api.data.statusCode, api.data.foutcode, api.data.bericht);

  // Een antwoord dat de envelop net niet haalt - een oudere API, een proxy die
  // een veld weglaat - houdt wél zijn Nederlandse zin en zijn foutcode; die zijn
  // meer waard dan "de server antwoordde met 503".
  if (body && typeof body === 'object' && 'foutcode' in body && 'bericht' in body) {
    const { foutcode, bericht } = body as { foutcode?: unknown; bericht?: unknown };
    if (typeof foutcode === 'string' && typeof bericht === 'string' && bericht.length > 0) {
      return new ApiFout(status, foutcode, bericht);
    }
  }

  if (body && typeof body === 'object' && 'message' in body) {
    const bericht = (body as { message?: unknown }).message;
    if (typeof bericht === 'string' && bericht.length > 0) {
      const code = (body as { code?: unknown }).code;
      return new ApiFout(status, typeof code === 'string' ? code : 'fout', bericht);
    }
  }
  return new ApiFout(status, 'onbekende_fout', `De server antwoordde met ${status}.`);
}

/**
 * Een storing, geen afwijzing: de database is weg of de server is onbereikbaar.
 * Dat is iets anders dan "u bent niet ingelogd", en hoort ook niet zo te lezen.
 */
export function isStoring(fout: unknown): boolean {
  if (!(fout instanceof ApiFout)) return false;
  return (
    fout.statusCode === 503 ||
    fout.statusCode === 0 ||
    fout.foutcode === 'database_onbereikbaar' ||
    fout.foutcode === 'geen_verbinding'
  );
}

export function foutBericht(fout: unknown): string {
  if (fout instanceof ApiFout) return fout.message;
  if (fout instanceof Error && fout.message) return fout.message;
  return 'Er ging iets mis.';
}
